type CacheEntry<T> = {
  expiresAt: number;
  value: T;
};

const valueCache = new Map<string, CacheEntry<unknown>>();
const inflightCache = new Map<string, Promise<unknown>>();

export const getCachedValue = <T>(key: string): T | null => {
  const entry = valueCache.get(key);
  if (!entry) return null;
  if (entry.expiresAt <= Date.now()) {
    valueCache.delete(key);
    return null;
  }
  return entry.value as T;
};

export const setCachedValue = <T>(key: string, value: T, ttlMs: number): T => {
  valueCache.set(key, {
    value,
    expiresAt: Date.now() + ttlMs,
  });
  return value;
};

export const invalidateCachedValue = (prefix: string): void => {
  Array.from(valueCache.keys()).forEach((key) => {
    if (key.startsWith(prefix)) valueCache.delete(key);
  });
  Array.from(inflightCache.keys()).forEach((key) => {
    if (key.startsWith(prefix)) inflightCache.delete(key);
  });
};

export const getCachedOrFetch = async <T>(
  key: string,
  ttlMs: number,
  fetcher: () => Promise<T>
): Promise<T> => {
  const cached = getCachedValue<T>(key);
  if (cached !== null) return cached;

  const inflight = inflightCache.get(key);
  if (inflight) return await inflight as T;

  const promise: Promise<T> = Promise.resolve().then(async () => {
    try {
      const value = await fetcher();
      if (inflightCache.get(key) === promise) setCachedValue(key, value, ttlMs);
      return value;
    } finally {
      if (inflightCache.get(key) === promise) inflightCache.delete(key);
    }
  });

  inflightCache.set(key, promise);
  return await promise;
};
